import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ButtonSizeOptions } from "@loanbossinc/component-library";
import MriRentCodesSection from "./MriRentCodesSection";
import moment from "moment";

import { actionCreators as vendorIntegrationsActionCreators } from "./vendorIntegrations.store";
import {
  IntegrationsWrapper,
  VendorsContainer,
  VendorWrapper,
  VendorName,
  Header,
  SubHeader,
  StyledSwitch,
  Description,
  LRWrapper,
  Section,
  StyledField,
  DateButtonWrapper,
  StyledPrimaryButton,
} from "./styles";

const MRI_VENDOR_ID = "30c31266-8ca0-4a86-ba7e-e0242b37f5e6";

const VendorSwitch = ({ sft, companyIntegrations, onInputSwitchChange }) => {
  const isEnabled = useMemo(() => Boolean(companyIntegrations.find(integration => integration.vendorId === sft.id)), [companyIntegrations]);

  return (
    <VendorWrapper>
      <VendorName>{sft.source_name}</VendorName>
      <StyledSwitch value={isEnabled} onChange={() => onInputSwitchChange(sft.id, isEnabled)} />
    </VendorWrapper>
  );
};

const LenderReportingSwitch  = ({ onInputSwitchChange, isCompanyReadyForLenderReporting }) => {
  return (
    <LRWrapper>
      <VendorName>Deals ready for Lender Compliance Reporting</VendorName>
      <StyledSwitch value={isCompanyReadyForLenderReporting} onChange={() => onInputSwitchChange(isCompanyReadyForLenderReporting)} />
    </LRWrapper>
  );
};

const VendorIntegrations = props => {
  const {
    user,
    sourceFileTypes,
    companyIntegrations,
    getSourceFileTypes,
    isIntegrationsLoading,
    getVendorIntegrationsForCompany,
    enableVendorIntegration,
    disableVendorIntegration,
    getLenderReportingActiveForCompany,
    isLenderReportingLoading,
    isCompanyReadyForLenderReporting,
    setLenderReportingActiveForCompany,
    getDealsByCompanyId,
    dealsByCompanyId,
    updateDealsForLenderReporting,
    deleteOverdueRgisByDate,
    recoverOverdueRgisByDate,
    isDeletingOverdueRgis,
    isRetrievingOverdueRgis,
    rentRollSettings,
    getRentRollSettings
  } = props;

  const companyId = user?.contact?.companyId;

  const [removeOverdueDate, setRemoveOverdueDate] = useState(null);
  const [retrieveOverdueDate, setRetrieveOverdueDate] = useState(null);

  useEffect(() => {
    if (companyId && !companyIntegrations) {
      getVendorIntegrationsForCompany({ companyId });
    }
    if (companyId && !sourceFileTypes) {
      getSourceFileTypes();
    }
  }, [companyId, companyIntegrations, sourceFileTypes]);

  useEffect(() => {
    if (isCompanyReadyForLenderReporting === null) {
      getLenderReportingActiveForCompany({ companyId });
    }
  }, [isCompanyReadyForLenderReporting]);

  const onInputSwitchChange = (vendorId, isEnabled) => {
    if (isEnabled) {
      const integration = companyIntegrations.find(int => int.vendorId === vendorId);
      if (integration) {
        disableVendorIntegration({ integrationId: integration.id });
      }
    } else {
      const vendorName = sourceFileTypes.find(sft => sft.id === vendorId)?.source_name;
      enableVendorIntegration({ data: { companyId, vendorId, vendorName } });
    }
  };

  const onLenderReportingSwitchChange = () => {
    const newValue = !isCompanyReadyForLenderReporting;
    setLenderReportingActiveForCompany({ companyId, isReady: newValue });
    if(newValue) {
      getDealsByCompanyId({ companyId });
    }
  };

  const handleDatePicker = (e) => {
    const { name, value } = e.target;
    name === "removeOverdueDate" ? setRemoveOverdueDate(value) : setRetrieveOverdueDate(value);
  };

  const handleRemoveClick = () => {
    const date = moment(removeOverdueDate).format("YYYY-MM-DD");
    deleteOverdueRgisByDate({ companyId, date });
  };

  const handleRetrieveClick = () => {
    const date = moment(retrieveOverdueDate).format("YYYY-MM-DD");
    recoverOverdueRgisByDate({ companyId, date });
  };

  useEffect(() => {
    if(dealsByCompanyId) {
      for (const deal of dealsByCompanyId) {
        updateDealsForLenderReporting({ id: deal.id });
      }
    }
  }, [dealsByCompanyId]);

  const isMriEnabled = useMemo(
    () =>
      Boolean(
        companyIntegrations?.find(
          (integration) => integration.vendorId === (rentRollSettings?.vendorId ?? MRI_VENDOR_ID)
        )
      ),
    [companyIntegrations, rentRollSettings]
  );

  useEffect(() => getRentRollSettings({ companyId }), [companyId]);

  

  return (
    <IntegrationsWrapper>
      <Header>Lender Compliance Settings</Header>
      <SubHeader>Vendor Integrations</SubHeader>
      <Description>Enable or disable vendor integrations.</Description>
      <VendorsContainer>
        {isIntegrationsLoading && <CircularProgress size={30} style={{ color: "#005AA9" }} />}
        {!isIntegrationsLoading &&
          sourceFileTypes &&
          companyIntegrations &&
          sourceFileTypes.map((sft) => (
            <VendorSwitch sft={sft} key={sft.id} companyIntegrations={companyIntegrations} companyId={companyId} onInputSwitchChange={onInputSwitchChange} />
          ))}
      </VendorsContainer>
      <SubHeader>Lender Compliance Reporting Dashboard</SubHeader>
      <Description>Enable or disable report groups on the dashboard</Description>
      {isLenderReportingLoading && <CircularProgress size={30} style={{ color: "#005AA9" }} />}
      {!isLenderReportingLoading && (
        <LenderReportingSwitch
          onInputSwitchChange={onLenderReportingSwitchChange}
          companyId={companyId}
          isCompanyReadyForLenderReporting={isCompanyReadyForLenderReporting}
        />
      )}
      <Section>
        <Description>Remove overdue reports from the dashboard for reports prior to this date</Description>
        <DateButtonWrapper>
          <StyledField
            value={removeOverdueDate}
            name="removeOverdueDate"
            type="date"
            label="Date"
            isEmpty={!removeOverdueDate || !moment(removeOverdueDate).isValid()}
            onChange={handleDatePicker}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: "9999-12-31" }}
          />
          <StyledPrimaryButton
            style={{
              marginLeft: "1.25em",
              backgroundColor: removeOverdueDate && moment(removeOverdueDate).isValid() && "#005AA9"
            }}
            label={isDeletingOverdueRgis ? "Deleting..." : "Remove reports"}
            disabled={!removeOverdueDate || !moment(removeOverdueDate).isValid() || isDeletingOverdueRgis}
            onClick={handleRemoveClick}
            size={ButtonSizeOptions.sm}
          />
        </DateButtonWrapper>
        <Description>Retrieve past overdue reports from the dashboard from this date onwards</Description>
        <DateButtonWrapper>
          <StyledField
            value={retrieveOverdueDate}
            name="retrieveOverdueDate"
            type="date"
            label="Date"
            isEmpty={!retrieveOverdueDate || !moment(retrieveOverdueDate).isValid()}
            onChange={handleDatePicker}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: "9999-12-31" }}
          />
          <StyledPrimaryButton
            style={{
              marginLeft: "1.25em",
              backgroundColor: retrieveOverdueDate && moment(retrieveOverdueDate).isValid && "#005AA9"
            }}
            label={isRetrievingOverdueRgis ? "Retrieving..." : "Retrieve reports"}
            disabled={!retrieveOverdueDate || !moment(retrieveOverdueDate).isValid || isRetrievingOverdueRgis}
            onClick={handleRetrieveClick}
            size={ButtonSizeOptions.sm}
          />
        </DateButtonWrapper>
      </Section>

      {isMriEnabled && (
        <MriRentCodesSection />
      )}
      
    </IntegrationsWrapper>
  );
};

const mapStateToProps = ({ vendorIntegrations, authentication: { user } }) => ({ ...vendorIntegrations, user });

const mapDispatchToProps = dispatch => bindActionCreators({ ...vendorIntegrationsActionCreators }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(VendorIntegrations);
